/**
 * §P.8 · Cumplimiento y transparencia.
 *
 * ## `granted` tiene tres estados, y confundir dos de ellos es el fallo de esta sección
 *
 * `true` concedido, `false` denegado y **`null` nunca preguntado**. Leer el tercero
 * como «dijo que no» lleva a un club a creer que una familia se negó a que su hija
 * salga en una foto cuando lo que pasa es que nadie se lo pidió — y a no pedírselo
 * nunca, porque ya cree tener la respuesta.
 *
 * Por eso aquí no hay booleano en ninguna parte: hay `EstadoDeConsentimiento` con
 * sus tres valores y un nombre distinto para cada uno en pantalla.
 *
 * ## El registro de accesos **nos incluye a nosotros**
 *
 * `api_first.md` §A: «todo acceso a datos de un club queda en `audit_log`, y el
 * club puede verlo». Es lo que hace defendible tener un backoffice que lo ve todo:
 * el club comprueba por sí mismo cuándo miramos su cuenta y por qué. Con datos de
 * menores, esa es la diferencia entre soporte y vigilancia, así que las entradas de
 * plataforma se marcan en vez de esconderse entre las suyas.
 */

/**
 * Tres estados, nunca dos.
 *
 * `sin_preguntar` es el que importa y el que no existiría con un booleano.
 */
export type EstadoDeConsentimiento = 'concedido' | 'denegado' | 'sin_preguntar';

export const estadoDeConsentimiento = (
  granted: boolean | null
): EstadoDeConsentimiento =>
  granted === null ? 'sin_preguntar' : granted ? 'concedido' : 'denegado';

export const ESTADOS_DE_CONSENTIMIENTO: Record<
  EstadoDeConsentimiento,
  { etiqueta: string; clase: string }
> = {
  concedido: { etiqueta: 'Sí', clase: 'pastilla-bien' },
  denegado: { etiqueta: 'No', clase: 'pastilla-mal' },
  // Ni verde ni rojo: no es una respuesta, es que falta la pregunta.
  sin_preguntar: { etiqueta: 'Sin preguntar', clase: 'pastilla-paused' },
};

export interface Consentimiento {
  readonly code: string;
  readonly label: string;
  readonly is_required: boolean;
  /** Ver la cabecera: **`null` no es «no»**. */
  readonly granted: boolean | null;
  readonly granted_at: Date | string | null;
  readonly granted_by_user_id: string | null;
  /** Puede venir cadena vacía, no solo `null`. Ver `nombreDeQuienFirmo`. */
  readonly granted_by_name: string | null;
  /**
   * **Siempre `null` en la fila que llega aquí**, y está por fidelidad al contrato.
   *
   * No hay operación de «retirar»: retirar es `setConsent(false)`, que cierra la fila
   * anterior —esa sí lleva `revoked_at`— y abre una nueva con `granted: false`. El
   * servidor solo devuelve la abierta, así que una retirada se ve como un «No», que
   * es lo correcto: la familia contestó.
   */
  readonly revoked_at: Date | string | null;
}

export interface ConsentimientosDeAlumno {
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly consents: readonly Consentimiento[];
  /** Los **códigos** de los obligatorios que faltan. Vacío es «al día». */
  readonly missing_required: readonly string[];
}

export interface ContratoDeEncargo {
  readonly id: string;
  readonly version: string;
  readonly signed_by_user_id: string | null;
  readonly signed_at: Date | string;
  readonly signer_name: string | null;
  readonly signer_role: string | null;
  /** Lo que convierte una fila en una firma que se puede defender. */
  readonly ip: string | null;
  readonly asset_id: string | null;
}

export interface EntradaDeAuditoria {
  readonly id: string;
  readonly actor_user_id: string | null;
  readonly actor_name: string | null;
  /** `platform` es una entrada nuestra. Ver `esAccesoDeKiap`. */
  readonly actor_role: string | null;
  readonly action: string;
  readonly entity_type: string | null;
  readonly entity_id: string | null;
  readonly ip: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly occurred_at: Date | string;
}

export interface SesionDeSoporte {
  readonly id: string;
  readonly staff_user_id: string;
  readonly staff_name: string | null;
  /** Obligatorio y escrito por una persona: es lo único que se lee de verdad. */
  readonly reason: string;
  readonly started_at: Date | string;
  readonly ended_at: Date | string | null;
  readonly minutes: number | null;
}

/** El nombre del alumno, entero y sin el hueco de un apellido que falta. */
export const nombreDeAlumno = (a: {
  first_name: string;
  last_name: string | null;
}): string => [a.first_name, a.last_name].filter(Boolean).join(' ');

/**
 * Quién concedió, o `null`.
 *
 * El servidor manda **cadena vacía** y no `null` cuando no hay nombre, así que un
 * `?? 'Alguien'` no salta y la pantalla pinta «Concedido por » con el hueco al
 * final. Se comprueba el contenido, no la ausencia.
 */
export const nombreDeQuienFirmo = (c: Consentimiento): string | null => {
  const n = c.granted_by_name?.trim();
  return n && n.length > 0 ? n : null;
};

/**
 * Si esta entrada del registro es **nuestra**, no del club.
 *
 * El acceso de plataforma se marca en vez de esconderse entre los del club: la
 * promesa de `api_first.md` no es «puedes leer un registro», es «puedes ver cuándo
 * entramos nosotros». Una entrada nuestra indistinguible de las suyas cumple la
 * letra y no la promesa.
 */
export const esAccesoDeKiap = (e: EntradaDeAuditoria): boolean =>
  e.actor_role === 'platform';

/**
 * Cuánto duró una sesión de soporte, en palabras.
 *
 * Una abierta **no dura cero**: dura «todavía abierta», que es lo único que hay que
 * mirar en esta pantalla. `minutes` a `null` con un `0` pintado diría lo contrario.
 */
export function duracionDeSoporte(s: SesionDeSoporte): string {
  if (s.ended_at === null) return 'Todavía abierta';
  if (s.minutes === null) return 'Cerrada';
  if (s.minutes < 1) return 'Menos de un minuto';
  if (s.minutes < 60) return `${s.minutes} min`;

  const horas = Math.floor(s.minutes / 60);
  const resto = s.minutes % 60;
  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}

/**
 * Las acciones del registro, en español.
 *
 * ## La lista es corta a propósito, y las de aquí están comprobadas
 *
 * La primera versión de esta tabla la escribí de memoria —`support.session.started`,
 * `export.full`— y **ninguna de esas cadenas existe**. El registro habría pintado el
 * código crudo de todo y yo habría creído que estaba traducido. Estas salen de
 * buscar los `INSERT INTO audit_log` y las constantes `admin.*` del backend.
 *
 * Solo están las que pueden caer en el registro **de un club**: el repositorio
 * filtra por `academy_id`, así que las sesenta acciones globales del backoffice
 * —trabajos, métricas, leads, planes— no llegan aquí nunca. Mantener las sesenta
 * sería mantener cincuenta traducciones que nadie va a leer, y que se quedarían
 * viejas sin que nada lo avise.
 *
 * Una que no esté sale **con su código crudo**. Un código feo se entiende a medias;
 * un hueco no se entiende nada, y en un registro de auditoría un hueco es peor que
 * feo — parece que se está escondiendo algo.
 */
const ACCIONES: Record<string, string> = {
  // Del club: las cuatro que deja el propio panel y las apps.
  'media.download': 'Descargó un fichero',
  'coach.bundle.download': 'Descargó las fichas de sus alumnos',
  'student.health.read': 'Miró una ficha médica',
  'checkin.delete': 'Borró un registro de entrada',

  // Nuestras. Son las que dan sentido a esta pantalla.
  'admin.support.open': 'Entró en modo soporte',
  'admin.support.close': 'Salió del modo soporte',
  'admin.support.list': 'Consultó las sesiones de soporte',
  'admin.academy.read': 'Miró la ficha del club',
  'admin.academy.update': 'Cambió datos del club',
  'admin.academy.suspend': 'Suspendió el club',
  'admin.academy.activate': 'Reactivó el club',
  'admin.academy.onboarding': 'Miró el estado del alta',
  'admin.academy.usage': 'Miró el uso del club',
  'admin.academy.timeline': 'Miró el histórico del club',
  'admin.health.read': 'Miró los indicadores de salud del club',
  'admin.audit.list': 'Consultó este mismo registro',
  'admin.consents.list': 'Consultó los consentimientos',
  'admin.data_requests.list': 'Consultó las solicitudes de RGPD',
  'admin.subscription.read': 'Miró la suscripción del club',
  'admin.subscription.change': 'Cambió la suscripción del club',
  'admin.subscription.cancel': 'Canceló la suscripción del club',
  'admin.subscription.start': 'Dio de alta la suscripción del club',
  'admin.payment.record': 'Anotó un pago del club',
  'admin.payments.list': 'Consultó los pagos del club',
  'admin.user.read': 'Miró la ficha de una persona',
};

export const etiquetaDeAccion = (accion: string): string =>
  ACCIONES[accion] ?? accion;

/* ── §P.8 fase 2 · Solicitudes de RGPD y exportación ─────────────────────── */

export type ClaseDeSolicitud = 'export' | 'delete' | 'rectify' | 'restrict';

export const CLASES_DE_SOLICITUD: Record<
  string,
  { etiqueta: string; explicacion: string }
> = {
  export: {
    etiqueta: 'Quiere sus datos',
    explicacion:
      'Derecho de acceso: pide copia de todo lo que el club guarda de su hijo.',
  },
  delete: {
    etiqueta: 'Quiere que se borren',
    explicacion:
      'Derecho de supresión. No siempre procede: hay datos que el club está obligado a conservar.',
  },
  rectify: {
    etiqueta: 'Quiere corregir algo',
    explicacion: 'Derecho de rectificación: un dato mal puesto.',
  },
  restrict: {
    etiqueta: 'Quiere limitar el uso',
    explicacion:
      'Derecho de limitación: que se conserven pero no se usen para ciertas cosas.',
  },
};

export const etiquetaDeClaseDeSolicitud = (clase: string): string =>
  CLASES_DE_SOLICITUD[clase]?.etiqueta ?? clase;

export type EstadoDeSolicitud =
  'received' | 'in_progress' | 'completed' | 'rejected';

export const ESTADOS_DE_SOLICITUD: Record<
  string,
  { etiqueta: string; clase: string }
> = {
  received: { etiqueta: 'Recibida', clase: 'pastilla-paused' },
  in_progress: { etiqueta: 'En curso', clase: 'pastilla-info' },
  completed: { etiqueta: 'Resuelta', clase: 'pastilla-bien' },
  // Rechazada **no es un fallo**: ver `SolicitudDeDatos`.
  rejected: { etiqueta: 'Rechazada', clase: 'pastilla' },
};

export const etiquetaDeEstadoDeSolicitud = (estado: string): string =>
  ESTADOS_DE_SOLICITUD[estado]?.etiqueta ?? estado;

/**
 * Una solicitud de derechos RGPD de una familia.
 *
 * ## `rejected` no es un fallo, es una resolución
 *
 * El contrato lo dice: «negarse a borrar los datos de un alumno con recibos
 * pendientes es legítimo, y lo que hace falta es que **quede escrito por qué**». Así
 * que no se pinta en rojo y el motivo es obligatorio en la pantalla aunque el
 * servidor lo acepte vacío: una negativa sin motivo es lo que no se puede defender
 * delante de la Agencia.
 *
 * ## El plazo es legal, no una métrica interna
 *
 * «El RGPD da un mes para responder, y pasarse no es una métrica interna: es una
 * sanción.» Por eso `days_to_due` en negativo se enseña en rojo y arriba.
 */
export interface SolicitudDeDatos {
  readonly id: string;
  readonly student_id: string | null;
  readonly student_name: string | null;
  readonly user_id: string | null;
  readonly user_name: string | null;
  readonly kind: ClaseDeSolicitud;
  readonly status: EstadoDeSolicitud;
  readonly requested_by: string | null;
  readonly requested_at: Date | string;
  readonly due_at: Date | string | null;
  readonly completed_at: Date | string | null;
  readonly result_asset_id: string | null;
  readonly notes: string | null;
  /** Negativo si el plazo ya pasó. Ver la cabecera. */
  readonly days_to_due: number | null;
}

/** Si la solicitud sigue esperando respuesta. */
export const estaAbierta = (s: SolicitudDeDatos): boolean =>
  s.status === 'received' || s.status === 'in_progress';

/**
 * Cómo va el plazo, en palabras.
 *
 * **Fuera de plazo se dice con esas palabras**, no con un número negativo: «-4 días»
 * obliga a interpretar un signo, y lo que hay que entender es que ya se incumplió.
 */
export function plazoEnPalabras(dias: number | null): string {
  if (dias === null) return 'Sin plazo puesto';
  if (dias < 0) {
    const pasados = Math.abs(dias);
    return pasados === 1
      ? 'Fuera de plazo desde ayer'
      : `Fuera de plazo desde hace ${pasados} días`;
  }
  if (dias === 0) return 'El plazo acaba hoy';
  return dias === 1 ? 'Queda un día' : `Quedan ${dias} días`;
}

/**
 * Lo urgente de una solicitud, para pintarla.
 *
 * Solo las **abiertas** tienen urgencia: una resuelta fuera de plazo ya no se puede
 * arreglar corriendo, y enseñarla en rojo entre las pendientes esconde las que sí.
 */
export function urgenciaDeSolicitud(
  s: SolicitudDeDatos
): 'vencida' | 'pronto' | 'tranquila' | null {
  if (!estaAbierta(s) || s.days_to_due === null) return null;
  if (s.days_to_due < 0) return 'vencida';
  return s.days_to_due <= 7 ? 'pronto' : 'tranquila';
}
